import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  DEFERRAL_ERROR_CODES,
  useCreateManualDeferralMutation,
} from '@/entities/deferral'
import { useAuthStore } from '@/shared/store/authStore'
import { getOrganizationIdFromUser } from '@/shared/utils/get-organization-id'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils'
import type { IApiResponse } from '@/shared/types/api'
import { temperatureDeferralSchema } from './temperature-deferral.schema'
import type { TemperatureDeferralFormValues } from './temperature-deferral.schema'

interface Options {
  visitId: string
  donorId: string
  defaultReason?: string
  onSuccess?: () => void
}

/** The manual-deferral endpoint takes a length in days, the form takes a date. */
function durationDaysUntil(date: string): number {
  const [year, month, day] = date.split('-').map(Number)
  const end = Date.UTC(year, month - 1, day)
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.max(0, Math.round((end - today) / 86_400_000))
}

/** 500630 / 500631 carry English-only messages, so they get our own text. */
const OWN_ERROR_TEXT: Record<string, string | undefined> = {
  [DEFERRAL_ERROR_CODES.REASON_NOT_ALLOWED]:
    'deferrals.errors.reason_not_allowed',
  [DEFERRAL_ERROR_CODES.REASON_PERMANENCE_MISMATCH]:
    'deferrals.errors.reason_permanent',
}

/** Maps a field key reported by the endpoint back onto its form field. */
const API_FIELD_TO_FORM_FIELD: Partial<
  Record<string, keyof TemperatureDeferralFormValues>
> = {
  note: 'reason',
  durationDays: 'deferral_end_date',
}

/**
 * Deferral raised at screening off an abnormal body temperature. It goes
 * through the manual-deferral endpoint — there is no examination behind it,
 * only the reason for turning the donor away and how long the deferral runs.
 */
export function useTemperatureDeferralForm({
  visitId,
  donorId,
  defaultReason = '',
  onSuccess,
}: Options) {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const schema = useMemo(() => temperatureDeferralSchema(t), [t])

  const { mutateAsync, isPending } = useCreateManualDeferralMutation(
    visitId,
    donorId,
  )

  const form = useForm<TemperatureDeferralFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: defaultReason, deferral_end_date: '' },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await mutateAsync({
        visitId,
        durationDays: durationDaysUntil(data.deferral_end_date),
        note: data.reason,
        organizationId: getOrganizationIdFromUser(user),
        createdByUserId: user?.sub ?? '',
        reasonCode: null,
      })
      toaster.success({
        title: t('donor_detail.screening.temperature_deferral.success'),
      })
      form.reset()
      onSuccess?.()
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)
      if (body?.errors[0]?.code === '200000') {
        body.errors[0]?.fields?.forEach((field) => {
          const fieldName = API_FIELD_TO_FORM_FIELD[field.key]
          if (fieldName) form.setError(fieldName, { message: field.value })
        })
        return
      }
      if (body?.errors[0]?.message) {
        body.errors.forEach((item) => {
          const ownText = OWN_ERROR_TEXT[item.code]
          toaster.error({ title: ownText ? t(ownText) : item.message })
        })
        return
      }
      toaster.error({
        title: t('donor_detail.screening.temperature_deferral.error'),
      })
    }
  })

  return { form, isPending, onSubmit }
}
